import React, {ChangeEvent, useEffect, useState} from "react";
import {EnumOption} from "../../../../enums/common";
import {createOptionListForSelectTag, postalPattern, postalTitle} from "../../../../helpers/utils/Utils";
import InputField from "../../../../components/common/form/InputField";
import SelectField from "../../../../components/common/form/SelectField";
import TextAreaField from "../../../../components/common/form/TextAreaField";
import {useGetAllStatesQuery} from "../../../data/states/statesApi";
import { useLazyGetAllCountyByStateAbbrQuery} from "../../../data/counties/countiesApi";

// Define types for the props of PersonForm
interface PersonFormProps {
    enums: { PropertyType: EnumOption[] };
    handleSubmitAction: (personObj: any) => void;
    undoAction?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    submitBtnText: string;
    obj: any;
    requirementsForInstrument?: any[];
    setUnsavedPersonInfo: any
}

// Reusable PersonForm component
export const PropertyInfoForm: React.FC<PersonFormProps> = ({
                                                                enums,
                                                                submitBtnText,
                                                                handleSubmitAction,
                                                                undoAction,
                                                                obj,
                                                                setUnsavedPersonInfo
                                                            }) => {

    const [property, setProperty] = useState<any>(obj);
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [isInitialChange, setIsInitialChange] = useState(true);
    const statesApiResult = useGetAllStatesQuery("");
    const [getCountyByStateIdApi, countyByStateIdResponse] = useLazyGetAllCountyByStateAbbrQuery();


    useEffect(() => {
        setProperty(obj);
    }, [obj]);

    useEffect(() => {
        if (property?.address?.state?.value){
            getCountyByStateIdApi(property?.address?.state?.value);
        }
    }, [property?.address?.state?.value]);


    const handleOnChange = (
        e: ChangeEvent<HTMLInputElement> | { name: string; value: any } | ChangeEvent<HTMLTextAreaElement>
    ) => {
        const { name, value } = "target" in e ? e.target : e;

        // Deep clone and set value using path like "address.zip"
        const setDeepValue = (obj: any, path: string[], value: any): any => {
            if (path.length === 1) {
                return { ...obj, [path[0]]: value };
            }

            const [key, ...rest] = path;
            return {
                ...obj,
                [key]: setDeepValue(obj[key] || {}, rest, value),
            };
        };

        const updatePersonObj = () => {
            setProperty((prev: any) => setDeepValue(prev, name.split("."), value));
        };

        if (isInitialChange) {
            setIsInitialChange(false);
            setTimeout(updatePersonObj, 20);
        } else {
            updatePersonObj();
        }

        setIsChanged(true);
        setUnsavedPersonInfo(true);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        handleSubmitAction(property);
        setProperty(obj);
        setIsChanged(false);
        setUnsavedPersonInfo(false);
    }

    return (
        <form onSubmit={handleSubmit}
              className={`border border-text-1 p-3 bg-text-0`}>
            <div className="grid grid-cols-12">
                <SelectField
                    label="Property Type"
                    value={property?.propertyType}
                    placeholder="Select Property Type..."
                    options={createOptionListForSelectTag(enums?.["PropertyType"], "value", "constant")}
                    onChange={(selectedOption: any) => handleOnChange({name: 'propertyType', value: selectedOption})}
                />
                <InputField
                    label="Tax Account No."
                    value={property?.accountNumber}
                    placeholder="Enter Tax Account No..."
                    type="text"
                    name={"accountNumber"}
                    onChange={handleOnChange}
                />
                <InputField
                    label="Address"
                    value={property?.address?.streetAddress}
                    placeholder="Enter Address..."
                    type="text"
                    name={"address.streetAddress"}
                    onChange={handleOnChange}
                    required
                />

                <SelectField
                    placeholder='Select State...'
                    value={property?.address?.state}
                    options={createOptionListForSelectTag(statesApiResult?.data, "stateName", "stateAbbreviation")}
                    onChange={(selectedOption) => {
                        handleOnChange({name: "address.state", value: selectedOption})
                    }}
                    label={"State"}
                    required={true}
                />
                <SelectField
                    placeholder='Select County...'
                    value={property?.countyName}
                    options={createOptionListForSelectTag(countyByStateIdResponse?.data, "countyName", "countyName")}
                    onChange={(selectedOption) => {
                        handleOnChange({name: "countyName", value: selectedOption})
                    }}
                    label={"County"}
                    required={true}

                />
                <InputField
                    label="City"
                    value={property?.address?.city}
                    placeholder="Enter City..."
                    type="text"
                    name={"address.city"}
                    onChange={handleOnChange}
                />
                <InputField
                    label="Zip Code"
                    value={property?.address?.zip}
                    placeholder="Enter Zipcode..."
                    type="text"
                    name={"address.zip"}
                    onChange={handleOnChange}
                    pattern={postalPattern} title={postalTitle}
                />

                <TextAreaField
                    label="Legal description"
                    value={property?.legalDescription}
                    placeholder="Enter Legal Description..."
                    name={"legalDescription"}
                    onChange={handleOnChange}
                />
            </div>
            {isChanged && (
                <div className="grid grid-cols-12 gap-2 mt-2">
                    <div className="col-span-12 flex items-end justify-end">
                        <button
                            type="button"
                            className="bg-text-3 rounded-md text-text-0 text-xs px-2 py-1 mr-3"
                            onClick={(e) => {
                                if (submitBtnText === "Add") {
                                    setProperty({});
                                } else {
                                    undoAction && undoAction(e);
                                }
                                setIsChanged(false);
                                setUnsavedPersonInfo(false);
                            }}>
                            Undo
                        </button>
                        <button
                            type="submit"
                            className="bg-highlight-0 rounded-md text-text-0 text-xs px-2 py-1"
                        >
                            {submitBtnText}
                        </button>
                    </div>
                </div>
            )}
        </form>
    );
};
